import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, LayoutGrid, Loader2, Plus, RotateCcw, Save, Search,
  ShieldAlert, Tags, X,
} from 'lucide-react';
import { apiFetch } from '../api';
import CompanyLogo from './CompanyLogo';
import PageNav from './PageNav';
import {
  buildDefaultCompanyConfig,
  getCompanySettings,
  resetCompanySettings,
  saveCompanySettings,
} from '../utils/companySettings';

const SECTIONS = [
  { key: 'overview', label: 'Names', desc: 'Other names to match', icon: LayoutGrid, title: 'Also known as', intro: 'Add other names, short forms or brands, so stories that use them still match this company.' },
  { key: 'keywords', label: 'Topic keywords', desc: 'Tag stories by topic', icon: Tags, title: 'Topic keywords', intro: 'Words that tag a story with a topic (legal, HR, financial...) and push it up the list.' },
  { key: 'severity', label: 'Risk phrases', desc: 'Rate how risky a story is', icon: ShieldAlert, title: 'Risk phrases', intro: 'If a story contains one of these phrases, it gets that risk level.' },
];

const SEVERITY_META = {
  CRITICAL: { bar: 'bg-[#bd2f2b]', bg: 'bg-[#fae8e7]', text: 'text-[#bd2f2b]', border: 'border-[#f1c9c7]' },
  HIGH: { bar: 'bg-[#bd2f2b]', bg: 'bg-[#fae8e7]', text: 'text-[#bd2f2b]', border: 'border-[#f1c9c7]' },
  MEDIUM: { bar: 'bg-[#c9a82b]', bg: 'bg-[#f7f0d2]', text: 'text-[#86690c]', border: 'border-[#ecdf9c]' },
  LOW: { bar: 'bg-[#4b6679]', bg: 'bg-[#e6edf2]', text: 'text-[#4b6679]', border: 'border-[#cfdbe3]' },
};

const KEYWORD_LEVELS = {
  critical: { label: 'Critical', value: 1, badge: 'bg-[#fae8e7] text-[#bd2f2b] border-[#f1c9c7]', dot: 'bg-[#bd2f2b]' },
  high: { label: 'High', value: 0.8, badge: 'bg-[#fae8e7] text-[#bd2f2b] border-[#f1c9c7]', dot: 'bg-[#bd2f2b]' },
  mid: { label: 'Mid', value: 0.55, badge: 'bg-[#f7f0d2] text-[#86690c] border-[#ecdf9c]', dot: 'bg-[#c9a82b]' },
  low: { label: 'Low', value: 0.25, badge: 'bg-[#e6edf2] text-[#4b6679] border-[#cfdbe3]', dot: 'bg-[#4b6679]' },
};

function countKeywords(keywords) {
  return Object.values(keywords || {}).reduce((n, cat) => n + Object.keys(cat || {}).length, 0);
}

function countSeverity(severity) {
  return Object.values(severity || {}).reduce((n, list) => n + (list?.length || 0), 0);
}

function keywordLevelFromWeight(weight) {
  const n = Number(weight);
  if (n >= 0.9) return 'critical';
  if (n >= 0.7) return 'high';
  if (n >= 0.4) return 'mid';
  return 'low';
}

function categoryLabel(category) {
  return String(category || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Category';
}

function slugCategory(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}


/* ─── Section: Overview ─── */
function OverviewSection({ settings, onChange, onGo, companyName }) {
  const [aliasInput, setAliasInput] = useState('');

  const addAlias = () => {
    const v = aliasInput.trim();
    if (!v || settings.aliases.some(a => a.toLowerCase() === v.toLowerCase())) return;
    onChange({ ...settings, aliases: [...settings.aliases, v] });
    setAliasInput('');
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {settings.aliases.length === 0 && <span className="text-[13px] text-[#76829a] py-1">No other names yet. {companyName} is matched by its own name only.</span>}
          {settings.aliases.map(a => (
            <span key={a} className="inline-flex items-center gap-1 pl-3 pr-1 h-8 rounded-md border border-[#e1e5ec] bg-[#f6f7fa] text-[13px] font-medium text-[#0d1524]">
              {a}
              <button
                type="button"
                aria-label={`Remove ${a}`}
                onClick={() => onChange({ ...settings, aliases: settings.aliases.filter(x => x !== a) })}
                className="w-6 h-6 grid place-items-center rounded hover:bg-[#fae8e7] hover:text-[#bd2f2b] text-[#76829a]"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-md mt-3">
          <input
            value={aliasInput}
            onChange={e => setAliasInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addAlias()}
            placeholder="Add a name, then press Enter"
            className="flex-1 text-[13px] border border-[#cbd3df] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
          />
          <button type="button" onClick={addAlias}
            className="inline-flex items-center gap-1.5 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] rounded-md hover:brightness-110 h-8">
            <Plus size={14} />Add
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Section: URLs ─── */

function KeywordsSection({ settings, categories, onChange }) {
  const baseCategories = useMemo(() => {
    const all = [...(categories || []), 'miscellaneous'];
    return [...new Set(all.map(slugCategory).filter(Boolean))];
  }, [categories]);

  const allCategories = useMemo(() => {
    const keys = Object.keys(settings.keywords || {}).map(slugCategory).filter(Boolean);
    return [...new Set([...baseCategories, ...keys])];
  }, [baseCategories, settings.keywords]);

  const [activeCat, setActiveCat] = useState(baseCategories[0] || 'legal');
  const [draft, setDraft] = useState({ keyword: '', level: 'high' });
  const [categoryDraft, setCategoryDraft] = useState('');

  useEffect(() => {
    if (!allCategories.includes(activeCat)) {
      setActiveCat(allCategories[0] || 'miscellaneous');
    }
  }, [activeCat, allCategories]);

  const keywords = settings.keywords || {};
  const rows = Object.entries(keywords[activeCat] || {})
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  const updateCategory = (category, nextRows) => {
    onChange({
      ...settings,
      keywords: {
        ...keywords,
        [category]: nextRows,
      },
    });
  };

  const addKeyword = () => {
    const keyword = draft.keyword.trim();
    const level = KEYWORD_LEVELS[draft.level] ? draft.level : 'high';
    if (!keyword) return;
    updateCategory(activeCat, {
      ...(keywords[activeCat] || {}),
      [keyword]: KEYWORD_LEVELS[level].value,
    });
    setDraft({ keyword: '', level: 'high' });
  };

  const updateKeywordLevel = (keyword, level) => {
    if (!KEYWORD_LEVELS[level]) return;
    updateCategory(activeCat, {
      ...(keywords[activeCat] || {}),
      [keyword]: KEYWORD_LEVELS[level].value,
    });
  };

  const removeKeyword = (keyword) => {
    const nextRows = { ...(keywords[activeCat] || {}) };
    delete nextRows[keyword];
    updateCategory(activeCat, nextRows);
  };

  const addCategory = () => {
    const category = slugCategory(categoryDraft);
    if (!category) return;
    onChange({
      ...settings,
      keywords: {
        ...keywords,
        [category]: keywords[category] || {},
      },
    });
    setActiveCat(category);
    setCategoryDraft('');
  };

  const removeCategory = (category) => {
    const count = Object.keys(keywords[category] || {}).length;
    if (baseCategories.includes(category) || count > 0) return;
    const nextKeywords = { ...keywords };
    delete nextKeywords[category];
    const nextCategories = allCategories.filter(item => item !== category);
    onChange({
      ...settings,
      keywords: nextKeywords,
    });
    if (activeCat === category) {
      setActiveCat(nextCategories[0] || 'miscellaneous');
    }
  };

  const activeIsDefault = baseCategories.includes(activeCat);

  return (
    <div className="space-y-4">
      {/* Topics */}
      <div className="flex flex-wrap items-center gap-2">
        {allCategories.map(category => {
          const active = activeCat === category;
          const count = Object.keys(keywords[category] || {}).length;
          const custom = !baseCategories.includes(category);
          return (
            <span
              key={category}
              className={`inline-flex items-center h-8 rounded-md border text-[13px] font-medium transition ${
                active ? 'bg-[#e7edfc] border-[#1c4ed8] text-[#1c4ed8]' : 'bg-white border-[#cbd3df] text-[#45516a] hover:bg-[#f6f7fa]'
              }`}
            >
              <button type="button" onClick={() => setActiveCat(category)} className="inline-flex items-center gap-2 h-full pl-3 pr-2.5">
                {categoryLabel(category)}
                <em className={`not-italic text-xs rounded px-1.5 py-px ${active ? 'bg-white text-[#1c4ed8]' : 'bg-[#f6f7fa] text-[#5b6780]'}`}>{count}</em>
              </button>
              {custom && (
                <button
                  type="button"
                  onClick={() => removeCategory(category)}
                  disabled={count > 0}
                  className={`h-full pr-2 ${count > 0 ? 'text-[#cbd3df] cursor-not-allowed' : 'text-[#76829a] hover:text-[#bd2f2b]'}`}
                  title={count > 0 ? 'Remove all keywords before deleting this topic' : 'Delete empty topic'}
                  aria-label={`Delete ${categoryLabel(category)}`}
                >
                  <X size={13} />
                </button>
              )}
            </span>
          );
        })}
        <span className="inline-flex items-center gap-1.5 ml-auto">
          <input
            value={categoryDraft}
            onChange={e => setCategoryDraft(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCategory()}
            placeholder="New topic"
            className="w-32 text-[13px] border border-[#cbd3df] rounded-md px-2.5 bg-white focus:outline-none focus:border-[#1c4ed8] h-8"
          />
          <button type="button" onClick={addCategory} title="Add topic" aria-label="Add topic"
            className="w-8 h-8 grid place-items-center rounded-md border border-[#cbd3df] text-[#45516a] hover:text-[#1c4ed8] hover:bg-[#e7edfc]">
            <Plus size={14} />
          </button>
        </span>
      </div>

      {/* Keywords for the chosen topic */}
      <div className="rounded-lg border border-[#e1e5ec] overflow-hidden">
        <div className="px-4 py-3 bg-[#f6f7fa] border-b border-[#e1e5ec] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[#0d1524]">{categoryLabel(activeCat)}</h3>
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-white border border-[#e1e5ec] text-[#45516a]">
              {activeIsDefault ? 'Default topic' : 'Custom topic'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={draft.keyword}
              onChange={e => setDraft(d => ({ ...d, keyword: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addKeyword()}
              placeholder={`Add a ${categoryLabel(activeCat).toLowerCase()} keyword`}
              className="w-56 text-[13px] border border-[#cbd3df] rounded-md px-2.5 bg-white focus:outline-none focus:border-[#1c4ed8] h-8"
            />
            <select
              value={draft.level}
              onChange={e => setDraft(d => ({ ...d, level: e.target.value }))}
              aria-label="Strength"
              className="text-[13px] font-medium border border-[#cbd3df] rounded-md px-2.5 bg-white focus:outline-none focus:border-[#1c4ed8] h-8"
            >
              {Object.entries(KEYWORD_LEVELS).map(([key, option]) => (
                <option key={key} value={key}>{option.label}</option>
              ))}
            </select>
            <button type="button" onClick={addKeyword}
              className="inline-flex items-center gap-1.5 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] rounded-md hover:brightness-110 transition h-8">
              <Plus size={14} />Add
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-12">
            <Tags size={24} className="mx-auto text-[#76829a] mb-2" />
            <p className="text-sm font-medium text-[#0d1524]">No keywords in {categoryLabel(activeCat)} yet</p>
            <p className="text-[13px] text-[#76829a] mt-1">Add one above and choose how strong it is.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-[#e1e5ec]">
            {rows.map(([keyword, weight]) => {
              const level = keywordLevelFromWeight(weight);
              const meta = KEYWORD_LEVELS[level];
              return (
                <div key={keyword} className="group flex items-center gap-3 px-4 py-2.5 border-b border-[#e1e5ec] hover:bg-[#f6f7fa]">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#0d1524]">{keyword}</span>
                  <select
                    value={level}
                    onChange={e => updateKeywordLevel(keyword, e.target.value)}
                    aria-label={`${keyword} strength`}
                    className="text-[13px] font-medium border border-[#cbd3df] rounded-md px-2 bg-white text-[#0d1524] focus:outline-none focus:border-[#1c4ed8] h-7"
                  >
                    {Object.entries(KEYWORD_LEVELS).map(([key, option]) => (
                      <option key={key} value={key}>{option.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                    className="w-7 h-7 grid place-items-center rounded-md text-[#76829a] hover:text-[#bd2f2b] hover:bg-[#fae8e7] opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                    title="Remove keyword"
                    aria-label={`Remove ${keyword}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Section: Severity (4-column board) ─── */
const SEVERITY_NOTE = {
  CRITICAL: 'Most urgent. Counted as high risk.',
  HIGH: 'Serious. Counted as high risk.',
  MEDIUM: 'Worth a look.',
  LOW: 'Background reporting.',
};

function SeveritySection({ settings, onChange }) {
  const [drafts, setDrafts] = useState({});

  const add = (level) => {
    const kw = (drafts[level] || '').trim();
    if (!kw) return;
    const list = settings.severity[level] || [];
    if (list.includes(kw)) return;
    onChange({ ...settings, severity: { ...settings.severity, [level]: [...list, kw] } });
    setDrafts(d => ({ ...d, [level]: '' }));
  };

  const remove = (level, kw) => {
    onChange({
      ...settings,
      severity: { ...settings.severity, [level]: (settings.severity[level] || []).filter(k => k !== kw) },
    });
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Object.entries(SEVERITY_META).map(([level, meta]) => {
          const items = settings.severity[level] || [];
          return (
            <div key={level} className="rounded-lg border border-[#e1e5ec] bg-white overflow-hidden flex flex-col min-h-[280px]">
              <div className={`h-1 ${meta.bar}`} />
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2">
                  <h4 className={`text-sm font-semibold capitalize ${meta.text}`}>{level.toLowerCase()}</h4>
                  <span className="ml-auto text-xs font-medium px-1.5 py-0.5 rounded bg-[#f6f7fa] border border-[#e1e5ec] text-[#45516a]">{items.length}</span>
                </div>
                <p className="mt-0.5 mb-3 text-xs text-[#76829a]">{SEVERITY_NOTE[level]}</p>
                <ul className="flex-1 space-y-1.5 overflow-y-auto max-h-48 mb-3">
                  {items.length === 0 ? (
                    <li className="text-[13px] text-[#76829a] py-4 text-center">No phrases yet</li>
                  ) : items.map(kw => (
                    <li key={kw} className="group flex items-center justify-between gap-2 bg-[#f6f7fa] border border-[#e1e5ec] rounded-md px-2.5 py-1.5 text-[13px] text-[#0d1524]">
                      <span className="truncate">{kw}</span>
                      <button type="button" aria-label={`Remove ${kw}`} onClick={() => remove(level, kw)}
                        className="p-0.5 text-[#76829a] hover:text-[#bd2f2b] opacity-0 group-hover:opacity-100 focus:opacity-100">
                        <X size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-1.5 mt-auto">
                  <input
                    value={drafts[level] || ''}
                    onChange={e => setDrafts(d => ({ ...d, [level]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && add(level)}
                    placeholder="Add a phrase"
                    className="flex-1 min-w-0 text-[13px] border border-[#cbd3df] rounded-md px-2.5 bg-white focus:outline-none focus:border-[#1c4ed8] h-8"
                  />
                  <button type="button" onClick={() => add(level)} aria-label={`Add ${level.toLowerCase()} phrase`}
                    className="w-8 h-8 grid place-items-center rounded-md border border-[#cbd3df] text-[#45516a] hover:text-[#1c4ed8] hover:bg-[#e7edfc]">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── People Settings ─── */

/* ─── Per-company search configuration (opened from the Companies page) ─── */
function CompanyConfig({ company, onBack, onFetch }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [settings, setSettings] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [section, setSection] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const categories = config?.categories || ['legal', 'regulatory', 'hr', 'criminal', 'financial'];
  const companyRef = useMemo(() => ({ name: company.name, aliases: company.aliases || [], urls: company.urls || [] }), [company]);

  const flash = useCallback((message, ms = 2500) => {
    setToast(message);
    setTimeout(() => setToast(''), ms);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Both requests are independent, so run them together.
        const [data, remote] = await Promise.all([
          apiFetch('/configuration/search'),
          apiFetch(`/companies/${encodeURIComponent(companyRef.name)}/settings`).catch(err => {
            console.error('Failed to load backend company settings', err);
            return null;
          }),
        ]);
        if (cancelled) return;
        setConfig(data);
        const fallback = getCompanySettings(companyRef.name, data, companyRef);
        const merged = remote
          ? {
            aliases: remote.aliases ?? fallback.aliases,
            urls: fallback.urls || [],
            keywords: remote.keywords || fallback.keywords,
            templates: remote.templates || fallback.templates,
            severity: remote.severity || fallback.severity,
          }
          : fallback;
        setSettings(merged);
        setBaseline(JSON.stringify(merged));
        if (remote) saveCompanySettings(companyRef.name, merged);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyRef, attempt]);

  const dirty = settings && baseline ? JSON.stringify(settings) !== baseline : false;

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await apiFetch(`/companies/${encodeURIComponent(company.name)}/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          aliases: settings.aliases || [],
          keywords: settings.keywords || {},
          templates: settings.templates || {},
          severity: settings.severity || {},
        }),
      });
      const saved = {
        aliases: updated.aliases ?? settings.aliases ?? [],
        urls: settings.urls || [],
        keywords: updated.keywords || settings.keywords || {},
        templates: updated.templates || settings.templates || {},
        severity: updated.severity || settings.severity || {},
      };
      saveCompanySettings(company.name, saved);
      setSettings(saved);
      setBaseline(JSON.stringify(saved));
      flash('Changes saved');
    } catch (err) {
      flash(`Save failed: ${err.message}`, 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (baseline) setSettings(JSON.parse(baseline));
  };

  const handleReset = () => {
    if (!config) return;
    resetCompanySettings(company.name);
    const defaults = buildDefaultCompanyConfig(config, companyRef);
    setSettings(defaults);
    flash('Defaults restored. Save to keep them.');
  };

  const activeSection = SECTIONS.find(x => x.key === section) || SECTIONS[0];
  const tabCounts = settings
    ? { keywords: countKeywords(settings.keywords), severity: countSeverity(settings.severity) }
    : {};

  return (
    <div className={dirty ? 'pb-16' : ''}>
      <PageNav backLabel="Companies" onBack={onBack} crumbs={[company.name, 'Settings']} />

      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <CompanyLogo name={company.name} size="lg" />
          <div className="min-w-0">
            <h1 className="text-2xl leading-tight font-semibold tracking-tight text-[#0d1524] truncate">{company.name} settings</h1>
            <p className="mt-1 text-sm text-[#45516a] max-w-[62ch]">
              Control how stories about {company.name} are found, tagged by topic and rated for risk.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {toast && (
            <span className={`text-[13px] ${toast.toLowerCase().includes('fail') ? 'text-[#bd2f2b]' : 'text-[#117049]'}`}>{toast}</span>
          )}
          <button type="button" onClick={() => onFetch(company.name)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] transition">
            <Search size={14} />Collect stories now
          </button>
          {settings && (
            <button type="button" onClick={handleReset}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] transition">
              <RotateCcw size={14} />Restore defaults
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#e1e5ec] rounded-lg flex flex-col items-center justify-center py-20 text-[#76829a]">
          <Loader2 size={24} className="animate-spin" />
          <p className="mt-3 text-sm">Loading {company.name} settings…</p>
        </div>
      ) : error ? (
        <div className="bg-white border border-[#e1e5ec] rounded-lg text-center py-16 px-6">
          <AlertCircle size={26} className="mx-auto text-[#bd2f2b]" />
          <p className="mt-2 text-sm font-medium text-[#0d1524]">Could not load settings</p>
          <p className="mt-1 text-[13px] text-[#76829a]">{error}</p>
          <button type="button" onClick={() => setAttempt(n => n + 1)}
            className="mt-4 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white hover:brightness-110">
            <RotateCcw size={14} />Try again
          </button>
        </div>
      ) : settings && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[232px_minmax(0,1fr)] gap-4 items-start">
            <nav aria-label="Settings sections" className="bg-white border border-[#e1e5ec] rounded-lg p-2 lg:sticky lg:top-[70px] flex lg:flex-col gap-1 overflow-x-auto">
              {SECTIONS.map(({ key, label, desc, icon: Icon }) => {
                const active = section === key;
                const count = tabCounts[key];
                return (
                  <button
                    key={key}
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setSection(key)}
                    className={`flex items-start gap-3 px-3 py-2.5 rounded-md text-left transition flex-none lg:flex-auto ${
                      active ? 'bg-[#e7edfc]' : 'hover:bg-[#f6f7fa]'
                    }`}
                  >
                    <Icon size={17} className={`mt-0.5 flex-none ${active ? 'text-[#1c4ed8]' : 'text-[#76829a]'}`} />
                    <span className="min-w-0 flex-1">
                      <b className={`block text-sm font-semibold ${active ? 'text-[#1c4ed8]' : 'text-[#0d1524]'}`}>{label}</b>
                      <small className="block text-xs text-[#76829a] whitespace-nowrap lg:whitespace-normal">{desc}</small>
                    </span>
                    <span className="text-xs font-medium text-[#45516a] bg-white border border-[#e1e5ec] rounded px-1.5 py-0.5 flex-none">
                      {count != null ? count : settings.aliases.length}
                    </span>
                  </button>
                );
              })}
            </nav>

            <div className="bg-white border border-[#e1e5ec] rounded-lg min-w-0">
              <div className="px-5 py-4 border-b border-[#e1e5ec]">
                <h2 className="text-[15px] font-semibold text-[#0d1524]">{activeSection.title}</h2>
                <p className="mt-0.5 text-[13px] text-[#76829a] max-w-[70ch]">{activeSection.intro}</p>
              </div>
              <div className="p-5">
                {section === 'overview' && <OverviewSection settings={settings} onChange={setSettings} onGo={setSection} companyName={company.name} />}
                {section === 'keywords' && <KeywordsSection settings={settings} categories={categories} onChange={setSettings} />}
                {section === 'severity' && <SeveritySection settings={settings} onChange={setSettings} />}
              </div>
            </div>
          </div>
        </>
      )}

      {dirty && (
        <div className="fixed bottom-0 left-0 right-0 md:left-[240px] z-30 bg-white border-t border-[#e1e5ec] px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_16px_rgba(10,17,31,0.06)]">
          <span className="text-sm text-[#0d1524]">
            <b className="font-semibold">You have unsaved changes</b>
            <span className="text-[#76829a]"> to {company.name}</span>
          </span>
          <span className="flex items-center gap-2">
            <button type="button" onClick={handleDiscard} disabled={saving}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] disabled:opacity-40 transition">
              Discard
            </button>
            <button type="button" onClick={handleSave} disabled={saving}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white hover:brightness-110 disabled:opacity-40 transition">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Save changes
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

export default CompanyConfig;
